import React, { useMemo } from 'react';
import { Form, Select, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

import AxaWarningAlert from '@/features/experiment/components/axa-warning-alert';
import DateTimePicker from '@/features/experiment/components/datetime-picker';
import {
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getExperimentStartAndEndTime,
  getVariables,
  isViewMode,
} from '@/features/experiment/constants';
import { useExperimentContext } from '@/features/experiment/contexts/experiment-context';
import { isAxaIncompatibleStartTime } from '@/features/experiment/utils';

const CustomTimeSlice = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const experimentAssignmentStrategy = getAssignmentStrategy(configurations);
  const [unixStart, unixEnd] = getExperimentStartAndEndTime(experiment);
  const formattedStartTime = unixStart ? dayjs.unix(unixStart) : undefined;
  const formattedEndTime = unixEnd ? dayjs.unix(unixEnd) : undefined;
  const variables = getVariables(configurations);
  const treatments = variables?.[0]?.choices;
  const isBalancedShuffleTimeSlice =
    experimentAssignmentStrategy ===
    ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE;
  const durationInDays = useMemo(() => {
    if (formattedStartTime && formattedEndTime) {
      return formattedEndTime.diff(formattedStartTime, 'days');
    }
    return undefined;
  }, [formattedStartTime, formattedEndTime]);

  const getExperimentLengthOptions = () => {
    const treatmentCount = treatments?.length || 0;
    const options = [1, 2, 3].map((i) => {
      const numberOfDays = i * 7 * treatmentCount;
      return {
        label: `${numberOfDays} days`,
        value: numberOfDays,
      };
    });

    return options;
  };

  const showAxaStartWarning =
    !readOnly && isAxaIncompatibleStartTime(formattedStartTime);

  if (!isBalancedShuffleTimeSlice) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Typography.Text strong>Start Date</Typography.Text>
        <Typography.Metadata type="secondary">
          Define the starting day and time of the experiment
        </Typography.Metadata>
      </div>

      <Form.Item
        validateStatus={!formattedStartTime ? 'error' : undefined}
        style={{ marginBottom: 0 }}
        rules={[{ required: true, message: '' }]}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Form.Item
              name={['startTime']}
              rules={[{ required: true, message: '' }]}
              style={{ marginBottom: 0 }}
            >
              <DateTimePicker />
            </Form.Item>
            <AxaWarningAlert show={showAxaStartWarning} type="startTime" />
          </div>
          {!!durationInDays && !isBalancedShuffleTimeSlice && (
            <div style={{ paddingLeft: 8 }}>
              <Typography.Text type="secondary">
                Duration: {durationInDays} days
              </Typography.Text>
            </div>
          )}
        </div>
      </Form.Item>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Typography.Text strong>
          Number of days to run the experiment
        </Typography.Text>
        <Typography.Metadata type="secondary">
          Define the number of days you desire to run this experiment. According
          to this, the end date will be generated.
        </Typography.Metadata>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Form.Item
          name="dayLength"
          style={{
            width: 450,
            margin: 0,
          }}
          validateStatus={!formattedEndTime ? 'error' : undefined}
          required={isBalancedShuffleTimeSlice}
        >
          <Select
            allowClear
            options={getExperimentLengthOptions()}
            disabled={readOnly}
          />
        </Form.Item>
        {formattedEndTime && (
          <Typography.Text type="secondary">
            (End date: {formattedEndTime.format('YYYY-MM-DD HH:mm Z')})
          </Typography.Text>
        )}
      </div>
    </div>
  );
};

export default CustomTimeSlice;
